import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import SERVICE_API_URL from '../serviceUrl';

const TestSubscriptionFlow = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [testDetails, setTestDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    console.log('TestSubscriptionFlow useEffect running');
    console.log('Location state:', location.state);
    
    // Check if test details are passed through navigation state first
    const stateTestDetails = location.state?.testDetails;
    
    if (stateTestDetails) {
      console.log('Using state data, no API call needed');
      // Use test details from navigation state
      setTestDetails({
        testId: stateTestDetails.testId,
        name: stateTestDetails.testName,
        description: stateTestDetails.testDescription,
        price: stateTestDetails.price,
        totalQuestions: stateTestDetails.totalQuestions,
        suggestedTimeInMinutes: stateTestDetails.suggestedTimeInMinutes
      });
      setLoading(false);
    } else {
      console.log('No state data, making API call');
      // Only fetch from API if no state data is available
      fetchTestDetails();
    }
  }, [testId, location.state]);

  const fetchTestDetails = async () => {
    console.log('fetchTestDetails called - making API call');
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      const response = await axios.get(`${SERVICE_API_URL}/prep/testId`, {
        params: {
          testId: testId,
          nextPageToken: null
        },
        headers: {
          "Authorization": `Bearer ${idToken}`
        }
      });

      // Check if this is a subscription response (no questions, but has test details)
      if (!response.data.questions && response.data.testName) {
        setTestDetails({
          testId: response.data.testId,
          name: response.data.testName,
          description: response.data.testDescription,
          price: response.data.price,
          totalQuestions: response.data.totalQuestions,
          suggestedTimeInMinutes: response.data.suggestedTimeInMinutes
        });
      } else {
        // User is already subscribed, redirect to test
        navigate(`/start-test/${testId}`);
        return;
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching test details:", error);
      setError("Failed to load test details. Please try again later.");
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    try {
      setProcessingPayment(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const response = await axios.post(
        `${SERVICE_API_URL}/prep/payment/create-order`,
        null,
        {
          params: { testIdentifier: testId },
          headers: {
            "Authorization": `Bearer ${idToken}`
          }
        }
      );

      const orderData = response.data;
      if (orderData.status === "already-unlocked") {
        navigate(`/start-test/${testId}`);
        return;
      }

      const order = orderData.order;
      if (!order) {
        throw new Error("Payment order was not returned by the backend");
      }
      const parsedOrder = JSON.parse(order);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: parsedOrder.amount,
        currency: "INR",
        name: "PrepNest",
        description: testDetails?.name || testId,
        order_id: parsedOrder.id,
        handler: function (response) {
          verifyPayment(response, parsedOrder.id);
        },
        theme: {
          color: "#3399cc",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Payment failed:", error);
      alert("Payment failed! Try again.");
    } finally {
      setProcessingPayment(false);
    }
  };

  const verifyPayment = async (response, orderId) => {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    try {
      const verificationResponse = await axios.post(
        `${SERVICE_API_URL}/prep/payment/verify-payment`,
        {
          orderId: orderId,
          testId: testId,
          paymentId: response.razorpay_payment_id,
          signature: response.razorpay_signature,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`
          }
        }
      );

      const verificationData = verificationResponse.data;
      if (verificationData.status === "success") {
        navigate(`/start-test/${testId}`);
      } else {
        alert("Payment verification failed!");
      }
    } catch (error) {
      console.error("Error verifying payment:", error);
      alert("Payment verification failed!");
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <h2>Loading test details...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card" style={{ 
          textAlign: 'center', 
          padding: '40px',
          backgroundColor: 'var(--error-light)',
          border: '1px solid var(--error)'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--spacing-md)'
          }}>
            <h2 style={{ color: 'var(--error)', marginBottom: 'var(--spacing-sm)' }}>
              Error Loading Test
            </h2>
            <p style={{ color: 'var(--text-primary)' }}>{error}</p>
            <button 
              onClick={() => navigate('/my-tests')}
              className="button"
              style={{ marginTop: 'var(--spacing-md)' }}
            >
              Back to Tests
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        {/* Test Header */}
        <div style={{
          borderBottom: '1px solid var(--border-color)',
          marginBottom: 'var(--spacing-xl)',
          paddingBottom: 'var(--spacing-lg)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-sm)'
          }}>
            <h2 style={{ 
              margin: 0,
              color: 'var(--primary-color)',
              fontSize: 'var(--font-size-2xl)'
            }}>
              {testDetails?.name}
            </h2>
            <div style={{
              display: 'flex',
              gap: 'var(--spacing-sm)',
              alignItems: 'center'
            }}>
              <span className="badge" style={{ backgroundColor: 'var(--primary-color)' }}>
                {testDetails?.totalQuestions} Questions
              </span>
              <span className="badge" style={{ backgroundColor: 'var(--secondary-color)' }}>
                {testDetails?.suggestedTimeInMinutes} Minutes
              </span>
            </div>
          </div>
          <p style={{ 
            margin: 0,
            color: 'var(--text-secondary)',
            fontSize: 'var(--font-size-md)'
          }}>
            {testDetails?.description}
          </p>
        </div>

        {/* Subscription Required Message */}
        <div style={{
          backgroundColor: 'var(--warning-light)',
          border: '1px solid var(--warning)',
          borderRadius: 'var(--border-radius)',
          padding: 'var(--spacing-lg)',
          marginBottom: 'var(--spacing-xl)',
          textAlign: 'center'
        }}>
          <h3 style={{ 
            color: 'var(--warning)',
            marginBottom: 'var(--spacing-md)'
          }}>
            {testDetails?.price && testDetails.price > 0 ? 'Subscription Required' : 'Access Required'}
          </h3>
          <p style={{ 
            color: 'var(--text-primary)',
            marginBottom: 'var(--spacing-lg)'
          }}>
            {testDetails?.price && testDetails.price > 0 
              ? 'This test requires a subscription to access. Subscribe now to start practicing!'
              : 'This test requires access. Click below to start practicing!'
            }
          </p>

          {/* Payment Button */}
          <button 
            onClick={handlePayment}
            disabled={processingPayment}
            className="button"
            style={{
              fontSize: 'var(--font-size-lg)',
              padding: 'var(--spacing-md) var(--spacing-xl)',
              backgroundColor: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--border-radius)',
              cursor: processingPayment ? 'not-allowed' : 'pointer',
              opacity: processingPayment ? 0.7 : 1
            }}
          >
            {processingPayment 
              ? 'Processing...' 
              : testDetails?.price && testDetails.price > 0 
                ? `Subscribe for ₹${testDetails.price}`
                : 'Start Test'
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestSubscriptionFlow; 
